/**
 * Key Metrics Cards for Accountant Report View
 * Displays Final Claim Amount, Variable Profit Loss, and Incremental Loss
 */

import React from 'react';
import { VerifiedValue } from '../shared/VerifiedValue';
import { FinancialValue } from '../shared/FinancialValue';

interface KeyMetricsProps {
  finalClaimAmount?: number;
  headlineLabel?: string;
  /** Stale report-snapshot figure; shown as a muted secondary line when it differs from the headline. */
  legacyAmount?: number | null;
  variableProfitLoss?: number;
  incrementalLoss?: number;
  causationPassed?: boolean;
}

export default function AccountantKeyMetrics({
  finalClaimAmount,
  headlineLabel = 'Final Claim Amount',
  legacyAmount,
  variableProfitLoss,
  incrementalLoss,
  causationPassed,
}: KeyMetricsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div
        className="rounded-xl p-6 shadow-lg"
        style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
      >
        <p className="text-sm opacity-90 mb-1">{headlineLabel}</p>
        <VerifiedValue
          value={finalClaimAmount ?? null}
          provenance="E"
          status={finalClaimAmount != null ? 'fresh' : 'unavailable'}
          size="xl"
          valueClassName="font-bold text-[color:var(--color-on-brand)]"
        />
        {legacyAmount != null && finalClaimAmount != null && Math.abs(legacyAmount - finalClaimAmount) > 0.01 && (
          <p className="text-xs mt-2 opacity-80">
            Prior report snapshot: <FinancialValue value={legacyAmount} cents compact /> — superseded by the figure above
          </p>
        )}
        {causationPassed !== undefined && (
          <p
            className="text-sm mt-2 opacity-90"
            style={{ color: causationPassed ? 'var(--color-verified)' : 'var(--color-blocker)' }}
          >
            {causationPassed ? 'Causation Passed' : 'Causation Failed'}
          </p>
        )}
      </div>

      <div
        className="rounded-xl p-6 shadow-lg"
        style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
      >
        <p className="text-sm opacity-90 mb-1">Variable Profit Loss</p>
        <VerifiedValue
          value={variableProfitLoss ?? null}
          provenance="E"
          status={variableProfitLoss != null ? 'fresh' : 'unavailable'}
          size="xl"
          valueClassName="font-bold text-[color:var(--color-on-brand)]"
        />
        <p className="text-sm mt-2 opacity-90">Step 1 Calculation</p>
      </div>

      <div
        className="rounded-xl p-6 shadow-lg"
        style={{ backgroundColor: 'var(--color-brand-hover)', color: 'var(--color-on-brand)' }}
      >
        <p className="text-sm opacity-90 mb-1">Incremental Loss</p>
        <VerifiedValue
          value={incrementalLoss ?? null}
          provenance="E"
          status={incrementalLoss != null ? 'fresh' : 'unavailable'}
          size="xl"
          valueClassName="font-bold text-[color:var(--color-on-brand)]"
        />
        <p className="text-sm mt-2 opacity-90">Step 2 Adjustment</p>
      </div>
    </div>
  );
}
